'use client';

import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useGlobal } from '@/contexts/GlobalContext';
import { useSdHeaderMenuDrawer } from '@/contexts/SdHeaderMenuDrawerContext';
import { useModal } from '@/contexts/ModalContext';
import { useConfirmModal } from '@/contexts/ConfirmModalContext';
import ConfirmModalContent from '@/components/layouts/ConfirmModalContent';
import TeamJoinModal from '@/components/teams/TeamJoinModal';
import LoginCard from '@/components/common/login-card/LoginCard';
import { TeamModel, Status } from '@/types/teams';
import OBOGLabel from '@/components/teams/OBOGLabel';
import { encodeUserId } from '@/utils/urlEncoder';
import { formatDate2 } from '@/utils/date';
import { getFilePath } from '@/utils';
import { isTeamConfigInitialized } from '@/utils/teams';

/**
 * ドロワー（SDヘッダーメニュー）
 * @param param0
 * @returns
 */
const SdHeaderMenuDrawer: React.FC = () => {
  const navigate = useNavigate();

  /**
   * React context
   */
  const context = useGlobal();
  const { closeDrawer } = useSdHeaderMenuDrawer();
  const { openConfirmModal } = useConfirmModal();
  const { openModal } = useModal();

  const userIdPathParam = () => {
    return context.user
      ? encodeUserId({
          accountName: context.user?.account_name,
          userId: context.user?.id
        })
      : undefined;
  };

  const handleMoveTo = (path: string) => {
    navigate(path);
    closeDrawer();
  };

  // チームの初期設定が完了していないかを判定する関数(単一チームのチェック)
  const isNotTeamConfigInitialized = (userTeam: TeamModel, userId: number | undefined): boolean => {
    return userTeam.owner_id === userId && !isTeamConfigInitialized(userTeam);
  };

  // チーム参加確認モーダルの表示
  const handleOpenConfirmTeamJoin = (team: TeamModel) => {
    openModal(<TeamJoinModal propsTeam={team} />);
  };

  // ログアウト
  const handleLogout = () => {
    window.location.href = import.meta.env.VITE_FOOTBALLLOG_LOGOUT_PATH;
  };

  // ログアウト確認
  const handleConfirmLogout = () => {
    openConfirmModal(
      <ConfirmModalContent onClickOKCallback={handleLogout} propsButtonType="outlinePrimary">
        ログアウトします。
        <br />
        よろしいですか？
      </ConfirmModalContent>
    );
  };

  const renderTicketStatus = () => {
    if (!context.user) {
      return <></>;
    }

    // チームプランのみ適用中の場合
    if (context.user.has_team_plan_only) {
      return <div className="text-xs text-left">（チームプラン適用中）</div>;
    }

    // チケット未適用かつチームプラン未適用の場合
    if (!context.user?.has_ticket && !context.user?.has_team_plan) {
      return <div className="text-xs text-left">（期限切れ）</div>;
    }

    // チケット適用中の場合は有効期限を優先表示（チーム適用中であっても有効期限を優先）
    if (context.user.has_ticket) {
      return <div className="text-xs text-left">（有効期限:&nbsp;{formatDate2(context.user.expiry_date)}）</div>;
    }

    return <></>;
  };

  const renderTeamsMenu = () => {
    if (context.user_teams?.length === 0) return <></>;
    return (
      <ul
        className="py-4 space-y-2 border-b"
        // style={context.isTeamPage ? {borderBottom: `solid 1px ${context.team?.color_a}`} : {}}
      >
        <li>
          <button
            type="button"
            onClick={() => handleMoveTo(`/home`)}
            className="flex items-center justify-start w-full text-lg text-gray-700 hover:bg-gray-900/[.03] px-6 py-2">
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0a8.949 8.949 0 0 0 4.951-1.488A3.987 3.987 0 0 0 13 16h-2a3.987 3.987 0 0 0-3.951 3.512A8.948 8.948 0 0 0 12 21Zm3-11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
              />
            </svg>
            <div className="ms-6">マイページ</div>
          </button>
        </li>
        {context.user_teams?.map((userTeam) => (
          <li key={userTeam.team.uuid}>
            {userTeam.status == Status.Requested ? (
              <div
                className="flex justify-between items-center w-full text-lg text-gray-700 hover:bg-gray-900/[.03] px-6 py-2"
                onClick={() => handleOpenConfirmTeamJoin(userTeam.team)}>
                <div className="flex items-center">
                  {userTeam.team.team_emblem ? (
                    <img
                      src={getFilePath(userTeam.team.team_emblem.file_path)}
                      alt="teamEmblem"
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <div className="text-white" style={{ color: userTeam.team.color_a }}>
                      <svg
                        className="w-6 h-6"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        viewBox="0 0 24 24">
                        <path d="M12.356 3.066a1 1 0 0 0-.712 0l-7 2.666A1 1 0 0 0 4 6.68a17.695 17.695 0 0 0 2.022 7.98 17.405 17.405 0 0 0 5.403 6.158 1 1 0 0 0 1.15 0 17.406 17.406 0 0 0 5.402-6.157A17.694 17.694 0 0 0 20 6.68a1 1 0 0 0-.644-.949l-7-2.666Z" />
                      </svg>
                    </div>
                  )}
                  <div
                    className={`ms-6
                      ${userTeam.team.name.length >= 1 && userTeam.team.name.length <= 10 && ' text-lg'}
                      ${userTeam.team.name.length >= 11 && userTeam.team.name.length <= 15 && ' text-base'}
                      ${userTeam.team.name.length >= 16 && ' text-sm'}
                    `}>
                    {userTeam.team.name}
                  </div>
                </div>
                <div className="w-3 h-3 rounded-full border border-white bg-blue-600"></div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleMoveTo(`/teams/${userTeam.team.uuid}`)}
                className="flex justify-between items-center w-full text-lg text-gray-700 hover:bg-gray-900/[.03] px-6 py-2">
                <div className="flex items-center">
                  {userTeam.team.team_emblem ? (
                    <img
                      src={getFilePath(userTeam.team.team_emblem.file_path)}
                      alt="teamEmblem"
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <div className="text-white" style={{ color: userTeam.team.color_a }}>
                      <svg
                        className="w-6 h-6"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        viewBox="0 0 24 24">
                        <path d="M12.356 3.066a1 1 0 0 0-.712 0l-7 2.666A1 1 0 0 0 4 6.68a17.695 17.695 0 0 0 2.022 7.98 17.405 17.405 0 0 0 5.403 6.158 1 1 0 0 0 1.15 0 17.406 17.406 0 0 0 5.402-6.157A17.694 17.694 0 0 0 20 6.68a1 1 0 0 0-.644-.949l-7-2.666Z" />
                      </svg>
                    </div>
                  )}
                  <div
                    className={`ms-6 text-start
                      ${userTeam.team.name.length >= 1 && userTeam.team.name.length <= 10 && ' text-lg'}
                      ${userTeam.team.name.length >= 11 && userTeam.team.name.length <= 15 && ' text-base'}
                      ${userTeam.team.name.length >= 16 && ' text-sm'}
                    `}>
                    {userTeam.team.name}
                    {!isTeamConfigInitialized(userTeam.team) && userTeam.team.owner_id === context.user?.id && (
                      <div className="text-xs">（チーム設定が完了していません）</div>
                    )}
                  </div>
                </div>
                {userTeam.status === Status.OBOG && (
                  <>
                    <OBOGLabel />
                  </>
                )}
                {isNotTeamConfigInitialized(userTeam.team, context.user?.id) && (
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                )}
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <div
        className="h-[100dvh] w-full bg-white flex flex-col justify-between bg-cover bg-no-repeat bg-right"
        style={{ backgroundImage: "url('/images/soccerball_gray.svg')", backgroundSize: '250%' }}>
        <div>
          <div className="p-1">
            <LoginCard propsUser={context.user} propsUserTeams={context.user_teams} />
          </div>
          {renderTeamsMenu()}
          <ul className="py-4 space-y-4">
            {context.isFeatureEnabled('ticket_payment') && (
              <li>
                <button
                  onClick={() => handleMoveTo(`/me/tickets`)}
                  type="button"
                  className="flex items-center justify-start w-full text-lg text-gray-700 hover:bg-gray-900/[.03] px-6 py-2">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9 17V17.5M9 6.5V7M9 13.5V14M9 10V10.5M18.5 12C18.5 11.337 18.7634 10.7011 19.2322 10.2322C19.7011 9.76339 20.337 9.5 21 9.5V7C21 6.73478 20.8946 6.48043 20.7071 6.29289C20.5196 6.10536 20.2652 6 20 6H4C3.73478 6 3.48043 6.10536 3.29289 6.29289C3.10536 6.48043 3 6.73478 3 7V9.5C3.66304 9.5 4.29893 9.76339 4.76777 10.2322C5.23661 10.7011 5.5 11.337 5.5 12C5.5 12.663 5.23661 13.2989 4.76777 13.7678C4.29893 14.2366 3.66304 14.5 3 14.5V17C3 17.2652 3.10536 17.5196 3.29289 17.7071C3.48043 17.8946 3.73478 18 4 18H20C20.2652 18 20.5196 17.8946 20.7071 17.7071C20.8946 17.5196 21 17.2652 21 17V14.5C20.337 14.5 19.7011 14.2366 19.2322 13.7678C18.7634 13.2989 18.5 12.663 18.5 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="ms-6">
                    footballLOGチケット
                    {renderTicketStatus()}
                  </div>
                </button>
              </li>
            )}
            <li>
              <button
                onClick={() => handleMoveTo(`/me/settings/profile-settings/?userid=${userIdPathParam()}`)}
                type="button"
                className="flex items-center justify-start w-full text-lg text-gray-700 hover:bg-gray-900/[.03] px-6 py-2">
                <svg
                  className="w-6 h-6 text-gray-800 dark:text-white"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24">
                  <path
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 19H5a1 1 0 0 1-1-1v-1a3 3 0 0 1 3-3h2m10 1a3 3 0 0 1-3 3m3-3a3 3 0 0 0-3-3m3 3h1m-4 3a3 3 0 0 1-3-3m3 3v1m-3-4a3 3 0 0 1 3-3m-3 3h-1m4-3v-1m-2.121 1.879-.707-.707m5.656 5.656-.707-.707m-4.242 0-.707.707m5.656-5.656-.707.707M12 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
                <div className="ms-6">設定</div>
              </button>
            </li>
          </ul>
        </div>
        <ul className="py-4 border-t">
          <li>
            <button
              type="button"
              className="flex items-center justify-start w-full text-sm text-gray-500 hover:bg-gray-900/[.03] px-4 py-3"
              onClick={handleConfirmLogout}>
              <svg
                className="w-4 h-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 12H8m12 0-4 4m4-4-4-4M9 4H7a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h2"
                />
              </svg>
              <span className="ms-2">ログアウト</span>
            </button>
          </li>
        </ul>
      </div>
    </>
  );
};

export default SdHeaderMenuDrawer;
